// Factual site content taken from the marina's existing published material.
// Nothing here is invented; anything uncertain lives in /app/memory/content-review.md.

export const SITE_NAME = "West Branch Marina";

export const LOCATIONS = {
  "west-branch": {
    slug: "west-branch",
    locationId: 8,
    name: "West Branch Boat Rentals",
    park: "West Branch State Park",
    streetAddress: "5678 Marina Dr",
    city: "Ravenna",
    region: "OH",
    postalCode: "44266",
    phone: "(330) 296-9209",
    phoneHref: "tel:+13302969209",
    onlineBooking: true,
  },
  "lake-milton": {
    slug: "lake-milton",
    locationId: 9,
    name: "Lake Milton Boat Rentals",
    park: "Lake Milton State Park",
    streetAddress: "16790 Milton Avenue",
    city: "Lake Milton",
    region: "OH",
    postalCode: "44429",
    phone: "(330) 654-4991",
    phoneHref: "tel:+13306544991",
    onlineBooking: true,
  },
};

/** Phone for a location: the /api/locations number first, then the site constant. */
export const locationPhone = (apiLoc, slug) => {
  if (apiLoc?.phone) return { phone: apiLoc.phone, href: `tel:+1${apiLoc.phone.replace(/\D/g, "")}` };
  const site = LOCATIONS[slug];
  return site ? { phone: site.phone, href: site.phoneHref } : null;
};

/** One-line postal address, e.g. "5678 Marina Dr, Ravenna, OH 44266". */
export const fullAddress = (loc) =>
  `${loc.streetAddress}, ${loc.city}, ${loc.region} ${loc.postalCode}`;

/** Plain maps URL -- no API key and no embedded third-party script. */
export const directionsUrl = (loc) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress(loc))}`;

export const FLEET_TYPES = [
  {
    name: "Pontoons",
    detail: "Standard, Class I, Class II deluxe and Class III deluxe pontoons.",
    image: "/images/family_pontoon_deck.jpeg",
    alt: "Family relaxing on the open deck of a pontoon boat",
  },
  {
    name: "VIP High-Horsepower Pontoons & Tritoons",
    detail: "90HP to 115HP models — the only boats cleared for towing watersports.",
    image: "/images/tritoon_high_hp.jpeg",
    alt: "High-horsepower tritoon running across open water",
  },
  {
    name: "Jet Skis & Waverunners",
    detail: "Personal watercraft for riders who want the throttle to themselves.",
    image: "/images/jetski_action.jpeg",
    alt: "Rider carving a turn on a jet ski",
  },
  {
    name: "Kayaks",
    detail: "Paddle the quiet coves at your own pace.",
    image: "/images/kayaks_calm_morning.jpeg",
    alt: "Kayaks lined up on a calm shoreline at sunrise",
  },
  {
    name: "Deck & Bow Boats",
    detail: "Open-layout boats with room to spread out.",
    image: "/images/marina_docks.jpeg",
    alt: "Boats moored along the marina docks",
  },
  {
    name: "Fishing Boats",
    detail: "Rigged for a morning on the water after walleye and bass.",
    image: "/images/courtesy_dock_evening.jpeg",
    alt: "Courtesy dock at the launch ramp in evening light",
  },
];

export const REQUIREMENTS = [
  {
    id: "age",
    title: "Age & driver's license",
    body: "Renters must be at least 18 years old and hold a valid driver's license.",
  },
  {
    id: "boater-education",
    title: "Ohio boater education",
    body: "Ohio residents born on or after January 1, 1982 must hold an Ohio boater's license, or pass a 10-question boating exam with at least 90% accuracy.",
  },
  {
    id: "cancellation",
    title: "Cancellation policy",
    body: "With the $20 Flexibility Fee — non-refundable, and it must be purchased at the time of the initial booking — you may cancel up to 24 hours in advance for a full rental refund. Without it, cancellation requires 72 hours notice and incurs a $75 fee. No-shows are charged full price. Same-day and hourly reservations under 24 hours are non-cancellable. Transfers to another day in the current season are possible by phone.",
  },
  {
    id: "weather",
    title: "Weather policy",
    body: "There is no refund for early returns due to weather. Rescheduling is handled by phone.",
  },
  {
    id: "pets",
    title: "Pets",
    body: "Pets are permitted on select vessels for a $20 fee, up to a maximum of 120 lbs.",
  },
  {
    id: "watersports",
    title: "Watersports & towing",
    body: "Towing tubes, skis and wakeboards is permitted only on select vessels — the VIP and deluxe pontoon models.",
  },
];

/** Primary nav, matching the live site's structure. `shortLabel` keeps the
 *  desktop row on ONE line; the full label is used in the mobile drawer. */
export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/boating-club", label: "Boating Club", shortLabel: "Boating Club" },
  { to: "/boat-rentals/west-branch", label: "Book Now West Branch", shortLabel: "Book West Branch" },
  { to: "/boat-rentals/lake-milton", label: "Book Now Lake Milton", shortLabel: "Book Lake Milton" },
  { to: "/contact", label: "Contact Us", shortLabel: "Contact" },
];

/** The live site's MORE dropdown, in the live site's order. */
export const MORE_LINKS = [
  { to: "/employment", label: "Employment" },
  { to: "/storage-and-docking", label: "Storage and Docking" },
  { to: "/winterization", label: "Winterization" },
  { to: "/boats-for-sale", label: "Boats For Sale" },
  { to: "/supplies", label: "Supplies" },
  { to: "/clean-marina", label: "Clean Marina" },
  { to: "/customer-photos", label: "Customer Photos" },
  { to: "/lake-information", label: "Lake Information" },
];

/** Real accounts, read off the live site's header icons. */
export const SOCIAL_LINKS = [
  { label: "Facebook", href: "https://www.facebook.com/westbranchmarina" },
  { label: "Instagram", href: "https://www.instagram.com/westbranchmarina/" },
];
